<template>
  <div class="order-data-modals">
    <Teleport to="body">
    <!-- ENVÍO A DOMICILIO / PARA RECOGER -->
    <div v-if="is_delivery_popup || is_take_away_popup" class="odm-overlay" @click.self="closePopUp" @keydown.esc="closePopUp">
      <section
        ref="sheet"
        class="odm-sheet"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`odm-title-${kind}`"
        tabindex="-1"
      >
        <header class="odm-header">
          <span class="odm-header__icon" aria-hidden="true">
            <i :class="isDelivery ? 'fa-solid fa-truck' : 'fa-solid fa-bag-shopping'"></i>
          </span>
          <div class="odm-header__text">
            <h2 :id="`odm-title-${kind}`">{{ isDelivery ? 'Datos de envío' : 'Datos para recoger' }}</h2>
            <p>{{ isDelivery ? 'Dinos dónde y cuándo te lo llevamos.' : 'Dinos a nombre de quién lo preparamos.' }}</p>
            <p v-if="payNext" class="odm-steps" aria-label="Paso 1 de 2">
              <span class="odm-steps__item is-current">1 · Tus datos</span>
              <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
              <span class="odm-steps__item">2 · Pago</span>
            </p>
          </div>
          <button type="button" class="odm-close" aria-label="Cerrar" @click="closePopUp">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </header>

        <form v-if="!showSuccessMessage" class="odm-form" novalidate @submit.prevent="submit">
          <div class="odm-body">
            <ScheduledTimePicker ref="schedule" v-model="scheduledFor" :token="token" />

            <fieldset class="odm-section">
              <legend class="odm-section__title">Tus datos</legend>
              <p v-if="prefilledFromLoyalty" class="odm-loyalty">
                <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
                Hemos rellenado tus datos de cliente fidelizado. Puedes cambiarlos.
              </p>
              <div class="odm-field">
                <label for="odm-name" class="odm-label">Nombre <span class="odm-req" aria-hidden="true">*</span></label>
                <input
                  id="odm-name" ref="name" v-model.trim="localUserInfo.name" type="text" class="odm-input"
                  autocomplete="name" maxlength="100" aria-required="true"
                  :aria-invalid="errors.name ? 'true' : 'false'" :aria-describedby="errors.name ? 'odm-name-error' : undefined"
                />
                <p v-if="errors.name" id="odm-name-error" class="odm-error">{{ errors.name }}</p>
              </div>
              <div class="odm-row">
                <div class="odm-field">
                  <label for="odm-phone" class="odm-label">
                    Teléfono <span v-if="isDelivery" class="odm-req" aria-hidden="true">*</span>
                    <span v-else class="odm-optional">(opcional)</span>
                  </label>
                  <input
                    id="odm-phone" ref="phone" v-model.trim="localUserInfo.phone" type="tel" class="odm-input"
                    autocomplete="tel" inputmode="tel" maxlength="20" :aria-required="isDelivery ? 'true' : 'false'"
                    :aria-invalid="errors.phone ? 'true' : 'false'" :aria-describedby="errors.phone ? 'odm-phone-error' : undefined"
                  />
                  <p v-if="errors.phone" id="odm-phone-error" class="odm-error">{{ errors.phone }}</p>
                </div>
                <div class="odm-field">
                  <label for="odm-email" class="odm-label">Email <span class="odm-optional">(opcional)</span></label>
                  <input
                    id="odm-email" ref="email" v-model.trim="localUserInfo.email" type="email" class="odm-input"
                    autocomplete="email" inputmode="email" maxlength="100" aria-describedby="odm-email-help"
                    :aria-invalid="errors.email ? 'true' : 'false'"
                  />
                  <p v-if="errors.email" class="odm-error">{{ errors.email }}</p>
                  <p v-else id="odm-email-help" class="odm-help">Te avisamos del estado del pedido.</p>
                </div>
              </div>
            </fieldset>

            <fieldset v-if="isDelivery" class="odm-section">
              <legend class="odm-section__title">Dirección de entrega</legend>
              <div v-if="!contextLoaded" class="odm-skeleton" aria-hidden="true"></div>
              <DeliveryAddressPicker
                v-else
                ref="address"
                v-model="addressValue"
                :restaurant="restaurantInfo"
              />
            </fieldset>

            <fieldset v-if="!info_pago_en_caja" class="odm-section">
              <legend class="odm-section__title">¿Cómo pagarás?</legend>
              <div class="odm-segmented" role="radiogroup" aria-label="Forma de pago">
                <label class="odm-segmented__option" :class="{ 'is-active': !localUserInfo.payWithCard }">
                  <input v-model="localUserInfo.payWithCard" type="radio" name="odm-pay" :value="false" class="visually-hidden-input" />
                  <i class="fa-solid fa-money-bill-wave" aria-hidden="true"></i> Efectivo
                </label>
                <label class="odm-segmented__option" :class="{ 'is-active': localUserInfo.payWithCard }">
                  <input v-model="localUserInfo.payWithCard" type="radio" name="odm-pay" :value="true" class="visually-hidden-input" />
                  <i class="fa-regular fa-credit-card" aria-hidden="true"></i> Tarjeta
                </label>
              </div>
            </fieldset>

            <div class="odm-field">
              <label for="odm-info" class="odm-label">
                {{ isDelivery ? 'Indicaciones para el repartidor' : 'Comentarios' }} <span class="odm-optional">(opcional)</span>
              </label>
              <textarea
                id="odm-info" v-model="localUserInfo.additionalInfo" class="odm-input odm-textarea" rows="3" maxlength="500"
                :placeholder="isDelivery ? 'Ej.: portal azul, llamar al timbre 3B' : 'Ej.: sin cebolla, pasaré a las 14:00'"
              ></textarea>
            </div>
          </div>

          <footer class="odm-footer">
            <p v-if="errorMessage" class="odm-form-error" role="alert">
              <i class="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {{ errorMessage }}
            </p>
            <button type="submit" class="odm-submit" :disabled="submitting">
              <i :class="submitting ? 'fa-solid fa-spinner fa-spin' : (payNext ? 'fa-regular fa-credit-card' : 'fa-solid fa-paper-plane')" aria-hidden="true"></i>
              {{ submitting ? 'Guardando…' : (payNext ? 'Continuar al pago' : 'Confirmar pedido') }}
            </button>
          </footer>
        </form>

        <div v-else class="odm-success" role="status">
          <i class="fa-solid fa-circle-check odm-success__icon" aria-hidden="true"></i>
          <h3>¡Pedido realizado!</h3>
          <div class="odm-success__box">
            <i class="fa-solid fa-cash-register" aria-hidden="true"></i>
            <p><strong>Por favor, diríjase al mostrador para realizar el pago</strong></p>
            <p>Su pedido empezará a prepararse una vez pagado</p>
          </div>
          <p class="odm-help">Redirigiendo a tus pedidos…</p>
        </div>
      </section>
    </div>

    <!-- BARRA / QUIOSCO -->
    <div v-if="is_counter_popup || is_kiosk_popup" class="odm-overlay" @click.self="closePopUp" @keydown.esc="closePopUp">
      <section ref="sheet" class="odm-sheet odm-sheet--small" role="dialog" aria-modal="true" aria-labelledby="odm-title-counter" tabindex="-1">
        <header class="odm-header">
          <span class="odm-header__icon" aria-hidden="true"><i class="fa-solid fa-circle-info"></i></span>
          <div class="odm-header__text">
            <h2 id="odm-title-counter">Dinos tu nombre</h2>
            <p>Te llamaremos cuando esté listo.</p>
          </div>
          <button type="button" class="odm-close" aria-label="Cerrar" @click="closePopUp">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </header>
        <form class="odm-form" novalidate @submit.prevent="submitPersonalInfoBarraQuiosco">
          <div class="odm-body">
            <div class="odm-field">
              <label for="odm-counter-name" class="odm-label">Nombre <span class="odm-req" aria-hidden="true">*</span></label>
              <input
                id="odm-counter-name" v-model.trim="localUserInfo.name" type="text" class="odm-input" autocomplete="name"
                maxlength="100" aria-required="true" :aria-invalid="errors.name ? 'true' : 'false'"
              />
              <p v-if="errors.name" class="odm-error">{{ errors.name }}</p>
            </div>
            <div class="odm-field">
              <label for="odm-counter-info" class="odm-label">Comentarios <span class="odm-optional">(opcional)</span></label>
              <textarea id="odm-counter-info" v-model="localUserInfo.additionalInfo" class="odm-input odm-textarea" rows="3" maxlength="500"></textarea>
            </div>
          </div>
          <footer class="odm-footer">
            <p v-if="errorMessage" class="odm-form-error" role="alert">{{ errorMessage }}</p>
            <button type="submit" class="odm-submit" :disabled="submitting">
              <i :class="payNext ? 'fa-regular fa-credit-card' : 'fa-solid fa-paper-plane'" aria-hidden="true"></i>
              {{ payNext ? 'Continuar al pago' : 'Confirmar pedido' }}
            </button>
          </footer>
        </form>
      </section>
    </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import DeliveryAddressPicker from '@/components/DeliveryAddressPicker.vue';
import ScheduledTimePicker from '@/components/ScheduledTimePicker.vue';

const PHONE_REGEX = /^\+?(\d{1,3})[-.\s]?(\d{1,4})[-.\s]?(\d{3})[-.\s]?(\d{3,4})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default {
  name: 'OrderDataModals',
  components: {
    DeliveryAddressPicker,
    ScheduledTimePicker,
  },
  props: {
    is_delivery_popup: Boolean,
    is_take_away_popup: Boolean,
    is_counter_popup: Boolean,
    is_kiosk_popup: Boolean,
    token: String,
    userInfo: {
      type: Object,
      default: () => ({
        name: null,
        address: null,
        email: null,
        additionalInfo: null,
        piso: null,
        codPostal: null,
        latitude: null,
        longitude: null,
        payWithCard: null,
      })
    },
    reward_id: {
      type: [String, Number],
      default: null
    },
    plate_reward_id: {
      type: [String, Number],
      default: null
    },
    info_pago_en_caja: {
      type: Boolean,
      default: false
    },
    // El restaurante cobra al pedir: tras guardar los datos viene el pago.
    payNext: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'success', 'order-error', 'loading', 'data-saved'],
  data() {
    return {
      localUserInfo: { ...this.userInfo, payWithCard: Boolean(this.userInfo?.payWithCard) },
      addressValue: {
        address: this.userInfo?.address || '',
        piso: this.userInfo?.piso || '',
        codPostal: this.userInfo?.codPostal || '',
        latitude: this.userInfo?.latitude ?? null,
        longitude: this.userInfo?.longitude ?? null,
      },
      errorMessage: '',
      errors: {},
      showSuccessMessage: false,
      scheduledFor: '',
      submitting: false,
      contextLoaded: false,
      restaurantInfo: {},
      prefilledFromLoyalty: false,
    };
  },
  computed: {
    isDelivery() {
      return Boolean(this.is_delivery_popup);
    },
    kind() {
      return this.isDelivery ? 'delivery' : 'take-away';
    },
    isOpen() {
      return Boolean(this.is_delivery_popup || this.is_take_away_popup);
    },
  },
  watch: {
    userInfo: {
      handler(newVal) {
        this.localUserInfo = { ...newVal, payWithCard: Boolean(newVal?.payWithCard) };
      },
      deep: true
    },
    isOpen: {
      handler(open) {
        if (open) this.onOpen();
      },
      immediate: true,
    },
    is_counter_popup(open) {
      if (open) this.focusFirst();
    },
    is_kiosk_popup(open) {
      if (open) this.focusFirst();
    },
  },
  methods: {
    focusFirst() {
      this.$nextTick(() => {
        const sheet = this.$refs.sheet;
        const input = sheet?.querySelector?.('input:not([type=radio])');
        if (input && !input.value) input.focus();
        else sheet?.focus?.();
      });
    },
    async onOpen() {
      this.errors = {};
      this.errorMessage = '';
      this.focusFirst();
      if (this.contextLoaded || !this.token) {
        this.contextLoaded = true;
        return;
      }
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/v1/api/app/checkout-context`, {
          headers: { Authorization: `Bearer ${this.token}` },
          timeout: 8000,
        });
        this.applyCheckoutContext(data || {});
      } catch (error) {
        // Sin contexto: el formulario funciona igual, solo sin autocompletar.
      } finally {
        this.contextLoaded = true;
      }
    },
    applyCheckoutContext(context) {
      this.restaurantInfo = context.restaurant || {};
      const customer = context.customer;
      if (customer) {
        let filled = false;
        for (const [field, value] of [['name', customer.name], ['email', customer.email], ['phone', customer.phone]]) {
          if (value && !this.localUserInfo[field]) {
            this.localUserInfo[field] = value;
            filled = true;
          }
        }
        this.prefilledFromLoyalty = filled;
      }
      const last = context.last_address;
      if (last && !this.addressValue.address) {
        this.addressValue = {
          address: last.address || '',
          piso: last.piso || '',
          codPostal: last.codPostal || '',
          latitude: last.latitude ?? null,
          longitude: last.longitude ?? null,
        };
      }
    },
    closePopUp() {
      this.$emit('close');
    },
    validatePersonalInfo() {
      const errors = {};
      const info = this.localUserInfo;
      if (!info.name) errors.name = 'Escribe tu nombre.';
      if (this.isDelivery && !PHONE_REGEX.test(info.phone || '')) errors.phone = 'Escribe un teléfono válido.';
      if (!this.isDelivery && info.phone && !PHONE_REGEX.test(info.phone)) errors.phone = 'Ese teléfono no parece válido.';
      if (info.email && !EMAIL_REGEX.test(info.email)) errors.email = 'Ese email no parece válido.';
      this.errors = errors;
      const first = Object.keys(errors)[0];
      if (first) this.$nextTick(() => this.$refs[first]?.focus());
      return first ? errors[first] : '';
    },
    buildUrl(path) {
      let url = process.env.VUE_APP_API_URL + path;
      const queryParams = [];
      if (this.reward_id) queryParams.push(`reward_id=${this.reward_id}`);
      if (this.plate_reward_id) queryParams.push(`plate_reward_id=${this.plate_reward_id}`);
      if (queryParams.length > 0) url += `?${queryParams.join('&')}`;
      return url;
    },
    submit() {
      this.errorMessage = '';
      const personalError = this.validatePersonalInfo();
      if (personalError) { this.errorMessage = personalError; return; }
      const addressError = this.isDelivery ? (this.$refs.address?.validate?.() || '') : '';
      if (addressError) { this.errorMessage = addressError; return; }
      if (this.isDelivery && !this.$refs.address) { this.errorMessage = 'Espera a que cargue el mapa.'; return; }
      const scheduleError = this.$refs.schedule?.validationError?.() || '';
      if (scheduleError) { this.errorMessage = scheduleError; return; }

      const info = this.localUserInfo;
      const payload = {
        name: info.name,
        phone: info.phone || null,
        email: info.email || null,
        additionalInfo: info.additionalInfo || null,
        payWithCard: this.info_pago_en_caja ? null : Boolean(info.payWithCard),
        scheduled_for: this.scheduledFor || null,
      };
      if (this.isDelivery) {
        const address = this.addressValue;
        Object.assign(payload, {
          address: address.address,
          piso: address.piso || null,
          codPostal: address.codPostal,
          latitude: address.latitude != null ? String(address.latitude) : null,
          longitude: address.longitude != null ? String(address.longitude) : null,
        });
      }
      this.sendCustomerData(this.isDelivery ? '/v1/api/app/update_delivery' : '/v1/api/app/update_take_away', payload);
    },
    sendCustomerData(path, payload) {
      this.submitting = true;
      this.$emit('loading', true);
      axios.put(this.buildUrl(path), payload, {
        headers: { 'Authorization': `Bearer ${this.token}` },
      })
        .then(response => {
          const finish = () => {
            // 201 = datos guardados y el pedido se crea al pagar: paso 2, el pago.
            if (response.status === 201) this.$emit('data-saved');
            else this.$emit('success', response.data);
          };
          if (this.info_pago_en_caja) {
            this.showSuccessMessage = true;
            setTimeout(finish, 7000);
          } else {
            finish();
          }
        })
        .catch(error => {
          const status = error.response?.status;
          if (status === 412) {
            this.errorMessage = 'Esta dirección queda fuera de la zona de reparto del restaurante.';
          } else if (status === 409 && error.response?.data?.message) {
            this.errorMessage = error.response.data.message;
          } else if (error.response?.data?.code === 'bdp_service_busy') {
            this.closePopUp();
            this.$emit('order-error', error);
          } else {
            this.errorMessage = 'No hemos podido enviar el pedido. Inténtalo de nuevo.';
            console.error(error);
          }
        })
        .finally(() => {
          this.submitting = false;
          this.$emit('loading', false);
        });
    },
    submitPersonalInfoBarraQuiosco() {
      this.errorMessage = '';
      this.errors = {};
      if (!this.localUserInfo.name) {
        this.errors = { name: 'Escribe tu nombre.' };
        this.errorMessage = 'Escribe tu nombre.';
        return;
      }
      this.submitting = true;
      this.$emit('loading', true);
      axios.put(this.buildUrl('/v1/api/app/update_barra_kiosk'), this.localUserInfo, {
        headers: { 'Authorization': `Bearer ${this.token}` },
      })
        .then(response => {
          if (response.status === 201) {
            this.$emit('data-saved');
          } else {
            this.$emit('success', response.data);
          }
        })
        .catch(error => {
          if (error.response && error.response.data?.code === 'bdp_service_busy') {
            this.closePopUp();
            this.$emit('order-error', error);
          } else {
            this.errorMessage = 'No hemos podido enviar el pedido. Inténtalo de nuevo.';
            console.log(error);
          }
        })
        .finally(() => {
          this.submitting = false;
          this.$emit('loading', false);
        });
    }
  }
}
</script>

<style scoped>
.odm-overlay {
  position: fixed; inset: 0; z-index: 2147483000; display: flex; align-items: center; justify-content: center;
  padding: 16px; background: rgba(15, 23, 42, 0.55);
}
.odm-sheet {
  display: flex; flex-direction: column; width: min(560px, 100%); max-height: calc(100dvh - 32px);
  border-radius: 20px; background: #fff; box-shadow: 0 30px 60px rgba(15, 23, 42, 0.3); overflow: hidden;
}
.odm-sheet--small { width: min(440px, 100%); }
.odm-sheet:focus { outline: none; }

.odm-header { display: flex; align-items: center; gap: 12px; padding: 18px 18px 14px; border-bottom: 1px solid #f1f5f9; }
.odm-header__icon {
  display: inline-flex; align-items: center; justify-content: center; flex: 0 0 44px; height: 44px; border-radius: 14px;
  background: color-mix(in srgb, var(--primary-color, #f47700) 14%, #fff); color: var(--primary-color, #f47700); font-size: 1.1rem;
}
.odm-header__text { flex: 1; min-width: 0; }
.odm-header__text h2 { margin: 0; font-size: 1.2rem; font-weight: 700; color: #111827; }
.odm-header__text p { margin: 2px 0 0; font-size: .85rem; color: #6b7280; }
.odm-steps { display: flex; align-items: center; gap: 8px; margin: 6px 0 0 !important; font-size: .78rem !important; color: #9ca3af !important; }
.odm-steps__item { font-weight: 600; }
.odm-steps__item.is-current { padding: 2px 8px; border-radius: 999px; background: var(--primary-color, #f47700); color: #fff; }
.odm-steps i { font-size: .6rem; }
.odm-close {
  flex: 0 0 40px; height: 40px; border: 0; border-radius: 50%; background: #f3f4f6; color: #374151; font-size: 1.05rem;
}
.odm-close:hover { background: #e5e7eb; }
.odm-close:focus-visible, .odm-submit:focus-visible { outline: 3px solid color-mix(in srgb, var(--primary-color, #f47700) 55%, #fff); outline-offset: 2px; }

.odm-form { display: flex; flex-direction: column; min-height: 0; flex: 1; }
.odm-body { flex: 1; min-height: 0; overflow-y: auto; padding: 16px 18px 8px; display: grid; gap: 18px; }

.odm-section { margin: 0; padding: 0; border: 0; min-width: 0; display: grid; gap: 12px; }
.odm-section__title { float: none; width: auto; margin: 0 0 2px; padding: 0; font-size: .78rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #6b7280; }

.odm-loyalty { display: flex; align-items: flex-start; gap: 8px; margin: 0; padding: 9px 12px; border-radius: 12px; background: #f0fdf4; color: #166534; font-size: .84rem; font-weight: 600; }
.odm-loyalty i { margin-top: 2px; }

.odm-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.odm-field { min-width: 0; }
.odm-label { display: block; margin-bottom: 6px; font-size: .86rem; font-weight: 600; color: #374151; }
.odm-req { color: #dc2626; }
.odm-optional { color: #9ca3af; font-weight: 500; }
.odm-input {
  width: 100%; min-height: 46px; padding: 10px 12px; border: 1.5px solid #d1d5db; border-radius: 12px;
  background: #fff; color: #111827; font-size: 1rem; transition: border-color .15s, box-shadow .15s;
}
.odm-input:focus { outline: none; border-color: var(--primary-color, #f47700); box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #f47700) 22%, transparent); }
.odm-input[aria-invalid='true'] { border-color: #dc2626; }
.odm-textarea { min-height: 84px; resize: vertical; }
.odm-help { margin: 6px 2px 0; font-size: .8rem; color: #6b7280; }
.odm-error { margin: 6px 2px 0; font-size: .8rem; color: #b91c1c; font-weight: 600; }

.odm-skeleton { height: 360px; border-radius: 14px; background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%); background-size: 200% 100%; animation: odm-shimmer 1.2s infinite; }
@keyframes odm-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

.odm-segmented { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.odm-segmented__option {
  position: relative; display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 46px;
  border: 1.5px solid #d1d5db; border-radius: 12px; background: #fff; color: #374151; font-weight: 600; cursor: pointer;
}
.odm-segmented__option.is-active { border-color: var(--primary-color, #f47700); background: color-mix(in srgb, var(--primary-color, #f47700) 10%, #fff); color: #111827; }
.odm-segmented__option:focus-within { box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #f47700) 30%, transparent); }
.visually-hidden-input { position: absolute; opacity: 0; width: 1px; height: 1px; pointer-events: none; }

.odm-footer { padding: 12px 18px 16px; border-top: 1px solid #f1f5f9; background: #fff; }
.odm-form-error { display: flex; align-items: flex-start; gap: 8px; margin: 0 0 10px; color: #b91c1c; font-size: .88rem; font-weight: 600; }
.odm-form-error i { margin-top: 3px; }
.odm-submit {
  display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; min-height: 52px; border: 0;
  border-radius: 14px; background: var(--primary-color, #f47700); color: #fff; font-size: 1.02rem; font-weight: 700;
  box-shadow: 0 10px 22px color-mix(in srgb, var(--primary-color, #f47700) 35%, transparent);
}
.odm-submit:disabled { opacity: .7; }

.odm-success { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px 24px; text-align: center; }
.odm-success__icon { font-size: 4.5rem; color: #16a34a; }
.odm-success h3 { margin: 0; font-weight: 700; }
.odm-success__box { width: 100%; padding: 18px; border-radius: 16px; background: linear-gradient(135deg, #fffbeb 0%, #fff3c4 100%); border: 2px solid #fee685; color: #92400e; }
.odm-success__box i { font-size: 2.4rem; color: #d07c06; }
.odm-success__box p { margin: 8px 0 0; }

@media (max-width: 575px) {
  .odm-overlay { align-items: flex-end; padding: 0; }
  .odm-sheet { width: 100%; max-height: 94dvh; border-radius: 22px 22px 0 0; }
  .odm-footer { padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)); }
  .odm-row { grid-template-columns: 1fr; }
}
</style>
